import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Images,
  Loader2,
  Pencil,
  Save,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import type { MediaAssetDTO } from "@app/shared";
import { api, mediaImageUrl } from "../lib/api";
import { useActiveBrand } from "../lib/brand";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { useToast } from "../components/Toast";

/** Human-readable file size. */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Media gallery: upload images per account and manage their metadata. When the
 * image source is set to "Galeri" (globally in Settings or per-generate), the AI
 * picks the most relevant ACTIVE asset for a post using the title / description /
 * tags — so good descriptions improve selection quality.
 */
export function Media() {
  const queryClient = useQueryClient();
  const toast = useToast();

  const accountKey = useActiveBrand();
  const [includeInactive, setIncludeInactive] = useState(false);

  // Upload form state.
  const [file, setFile] = useState<File | null>(null);
  const [fileKey, setFileKey] = useState(0); // bump to reset the file <input>
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState("");

  const mediaQuery = useQuery({
    queryKey: ["media", accountKey, includeInactive],
    queryFn: ({ signal }) =>
      api.media({ accountKey: accountKey!, includeInactive }, signal),
    enabled: !!accountKey,
  });

  const upload = useMutation({
    mutationFn: () => {
      if (!accountKey) throw new Error("Pilih brand terlebih dahulu");
      if (!file) throw new Error("Pilih file gambar dulu.");
      const form = new FormData();
      // Text fields FIRST, file LAST (so the server has all fields when parsing).
      form.append("accountKey", accountKey);
      form.append("title", title.trim());
      form.append("description", description.trim());
      form.append("tags", tags.trim());
      form.append("file", file);
      return api.uploadMedia(form);
    },
    onSuccess: () => {
      setFile(null);
      setFileKey((k) => k + 1);
      setTitle("");
      setDescription("");
      setTags("");
      void queryClient.invalidateQueries({ queryKey: ["media"] });
      toast.success("Gambar diunggah ke galeri");
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Upload gagal"),
  });

  const assets = mediaQuery.data ?? [];
  const canUpload = !!accountKey && file !== null && !upload.isPending;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight text-navy">
            <Images className="h-6 w-6 text-teal" />
            Galeri gambar
          </h1>
          <p className="text-sm text-slate-500">
            Unggah gambar Anda sendiri. Saat sumber gambar = “Galeri”, AI akan
            memilih gambar paling relevan dari sini untuk tiap post.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Unggah gambar</CardTitle>
          <CardDescription>
            PNG, JPG, atau WEBP (maks 10 MB). Beri judul, deskripsi, dan tag yang
            jelas — itu yang dipakai AI untuk mencocokkan gambar dengan konten.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="media-file">File gambar</Label>
              <input
                key={fileKey}
                id="media-file"
                type="file"
                accept="image/png,image/jpeg,image/webp"
                disabled={upload.isPending}
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                className="flex w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm file:mr-3 file:rounded-md file:border-0 file:bg-navy file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-white hover:file:bg-navy/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="media-title">Judul</Label>
              <Input
                id="media-title"
                placeholder="mis. Tim di ruang kerja modern"
                value={title}
                disabled={upload.isPending}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="media-desc">Deskripsi (untuk pencocokan AI)</Label>
            <Textarea
              id="media-desc"
              rows={2}
              placeholder="Jelaskan isi gambar & cocok untuk topik apa, mis. ‘Suasana kantor modern, dashboard data — cocok untuk transformasi digital’."
              value={description}
              disabled={upload.isPending}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="media-tags">Tag (pisahkan dengan koma)</Label>
            <Input
              id="media-tags"
              placeholder="kantor, data, teknologi, tim"
              value={tags}
              disabled={upload.isPending}
              onChange={(e) => setTags(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="primary"
              onClick={() => upload.mutate()}
              disabled={!canUpload}
            >
              {upload.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
              Unggah
            </Button>
            {file ? (
              <span className="text-xs text-slate-500">
                {file.name} · {formatBytes(file.size)}
              </span>
            ) : null}
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">
          {assets.length} gambar{" "}
          <span className="capitalize">({accountKey})</span>
        </p>
        <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
            className="h-4 w-4 rounded border-slate-300 text-teal focus:ring-teal"
          />
          Tampilkan yang non-aktif
        </label>
      </div>

      {mediaQuery.isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-teal" />
        </div>
      ) : mediaQuery.isError ? (
        <Card>
          <CardContent className="p-8 text-center text-sm text-coral">
            Gagal memuat galeri.
          </CardContent>
        </Card>
      ) : assets.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center text-sm text-slate-500">
            Belum ada gambar untuk akun ini. Unggah gambar pertama Anda di atas.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {assets.map((asset) => (
            <AssetCard key={asset.id} asset={asset} />
          ))}
        </div>
      )}
    </div>
  );
}

/** One gallery asset: image preview + inline metadata editor + active/delete. */
function AssetCard({ asset }: { asset: MediaAssetDTO }) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(asset.title);
  const [description, setDescription] = useState(asset.description);
  const [tags, setTags] = useState(asset.tags.join(", "));

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ["media"] });

  const save = useMutation({
    mutationFn: () =>
      api.updateMedia(asset.id, {
        title: title.trim() || asset.title,
        description: description.trim(),
        tags: tags
          .split(",")
          .map((t) => t.trim())
          .filter((t) => t.length > 0),
      }),
    onSuccess: () => {
      setEditing(false);
      invalidate();
      toast.success("Tersimpan");
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Gagal menyimpan"),
  });

  const toggleActive = useMutation({
    mutationFn: () => api.updateMedia(asset.id, { active: !asset.active }),
    onSuccess: () => invalidate(),
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Gagal mengubah status"),
  });

  const remove = useMutation({
    mutationFn: () => api.deleteMedia(asset.id),
    onSuccess: () => {
      invalidate();
      toast.success("Gambar dihapus");
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Gagal menghapus"),
  });

  const cancelEdit = () => {
    setTitle(asset.title);
    setDescription(asset.description);
    setTags(asset.tags.join(", "));
    setEditing(false);
  };

  return (
    <Card className={asset.active ? undefined : "opacity-60"}>
      <div className="relative aspect-[4/5] overflow-hidden rounded-t-xl bg-slate-100">
        <img
          src={mediaImageUrl(asset)}
          alt={asset.title}
          className="h-full w-full object-cover"
          loading="lazy"
        />
        {!asset.active ? (
          <Badge variant="secondary" className="absolute left-2 top-2">
            Non-aktif
          </Badge>
        ) : null}
      </div>
      <CardContent className="space-y-3 p-4">
        {editing ? (
          <div className="space-y-2">
            <Input
              value={title}
              placeholder="Judul"
              onChange={(e) => setTitle(e.target.value)}
            />
            <Textarea
              rows={2}
              value={description}
              placeholder="Deskripsi (untuk pencocokan AI)"
              onChange={(e) => setDescription(e.target.value)}
            />
            <Input
              value={tags}
              placeholder="tag, pisahkan, koma"
              onChange={(e) => setTags(e.target.value)}
            />
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="primary"
                onClick={() => save.mutate()}
                disabled={save.isPending}
              >
                {save.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                Simpan
              </Button>
              <Button size="sm" variant="outline" onClick={cancelEdit}>
                <X className="h-4 w-4" />
                Batal
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div>
              <p className="line-clamp-1 font-medium text-slate-900">
                {asset.title}
              </p>
              {asset.description ? (
                <p className="line-clamp-2 text-xs text-slate-500">
                  {asset.description}
                </p>
              ) : (
                <p className="text-xs italic text-slate-400">
                  Tanpa deskripsi — AI sulit mencocokkan.
                </p>
              )}
            </div>
            {asset.tags.length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {asset.tags.map((t) => (
                  <Badge key={t} variant="secondary">
                    {t}
                  </Badge>
                ))}
              </div>
            ) : null}
            <p className="text-[11px] text-slate-400">
              {asset.mimeType.replace("image/", "").toUpperCase()} ·{" "}
              {formatBytes(asset.sizeBytes)}
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setEditing(true)}
              >
                <Pencil className="h-4 w-4" />
                Edit
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => toggleActive.mutate()}
                disabled={toggleActive.isPending}
              >
                {toggleActive.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : null}
                {asset.active ? "Non-aktifkan" : "Aktifkan"}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="text-coral hover:text-coral"
                onClick={() => {
                  if (window.confirm(`Hapus "${asset.title}"?`)) remove.mutate();
                }}
                disabled={remove.isPending}
              >
                {remove.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
                Hapus
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
